import React from "react";
import { Link } from "react-router-dom";
import { Bowtie, ArrowRight } from "./icons";

const PILLARS = [
  { letter: "R", label: "RESILIENCE" },
  { letter: "H", label: "HEART" },
  { letter: "T", label: "TRANSFORMATION" },
];

/**
 * "Have a Story That Could Inspire Someone?" — companion CTA to FinalCTA,
 * but blue-accented to differentiate from the gold revenue-system block.
 * Recruits guests for The Hustle Zone podcast.
 */
export default function HustleZoneCTA({ to = "/the-hustle-zone" }) {
  return (
    <section data-testid="hustle-zone-cta" className="relative py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div
          className="relative rounded-3xl overflow-hidden p-8 md:p-16 border border-blue-400/30"
          style={{
            background:
              "radial-gradient(ellipse at top left, rgba(56,135,255,0.18), transparent 60%), linear-gradient(135deg, #050a14 0%, #04060c 100%)",
          }}
        >
          <svg
            className="absolute inset-0 w-full h-full opacity-30 pointer-events-none"
            viewBox="0 0 1200 600"
            preserveAspectRatio="none"
          >
            <line x1="0" y1="0" x2="1200" y2="100" stroke="#3B82F6" strokeWidth="0.5" />
            <line x1="0" y1="600" x2="1200" y2="500" stroke="#3B82F6" strokeWidth="0.5" />
            <line x1="0" y1="0" x2="200" y2="600" stroke="#3B82F6" strokeWidth="0.5" />
            <line x1="1200" y1="0" x2="1000" y2="600" stroke="#3B82F6" strokeWidth="0.5" />
          </svg>

          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-blue-300/90 px-4 py-1.5 rounded-full border border-blue-400/30 bg-black/40 backdrop-blur-sm">
              <Bowtie className="w-3 h-3 text-blue-300" />
              The Hustle Zone Podcast
            </span>
            <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Have a Story That Could<br />
              <span className="italic" style={{
                background: "linear-gradient(135deg, #93C5FD 0%, #3B82F6 50%, #1D4ED8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                Inspire Someone?
              </span>
            </h2>

            <p className="mt-7 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              <span className="text-white/90">The Hustle Zone</span> is looking for guests with real stories of
              resilience, entrepreneurship, neurodiversity, transformation, and heart-led success.
            </p>
            <p className="mt-4 text-white/60 text-base leading-relaxed max-w-2xl mx-auto">
              If you have overcome adversity, built something meaningful, or learned lessons that could help
              someone else move forward, <span className="text-white/85">Ryan wants to hear from you</span>.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {PILLARS.map((p) => (
                <div key={p.letter} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full border border-blue-400/40 flex items-center justify-center font-display text-blue-300 text-lg">
                    {p.letter}
                  </span>
                  <span className="font-display text-base md:text-lg tracking-wide text-white/85">
                    {p.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={to}
                data-testid="hustle-zone-apply"
                className="group inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full text-white text-xs uppercase tracking-[0.24em] font-bold hover:scale-[1.03] transition-transform min-w-[260px]"
                style={{
                  background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                  boxShadow: "0 0 30px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.18)",
                }}
              >
                <Bowtie className="w-4 h-4" />
                Become a Guest
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to={to}
                data-testid="hustle-zone-apply-secondary"
                className="text-sm text-blue-200/85 hover:text-blue-100 transition-colors underline-offset-4 hover:underline"
              >
                Apply to Share Your Story →
              </Link>
            </div>

            <div className="mt-8 text-white/40 text-xs">
              Independent · Long-form · Real conversations · Guest-friendly process
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
